"use client";

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type CompanyDetail = {
  id: number;
  name: string;
  gstin?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  is_active?: number;
  created_at?: string | null;
  updated_at?: string | null;
  users_count?: number | string;
  sales_count?: number | string;
};

export default function ProductAdminCompanyDetailPage() {
  const { id } = useParams();
  const companyId = Number(id ?? 0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["product-admin", "companies", companyId],
    enabled: Number.isFinite(companyId) && companyId > 0,
    queryFn: async () => {
      const { data } = await api.get<{ company: CompanyDetail }>(`/api/product-admin/companies/${companyId}`);
      return data.company;
    },
  });

  if (!Number.isFinite(companyId) || companyId <= 0) {
    return <p className="text-sm text-destructive">Invalid company id.</p>;
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading company…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-destructive">Could not load company.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.name}</h1>
          <p className="text-sm text-muted-foreground">Company details (product admin only).</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/product-admin/companies">Back</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground">Company name</p>
            <p className="font-medium">{data.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground">GSTIN</p>
            <p className="font-medium">{data.gstin ?? ""}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium">{data.email ?? ""}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Mobile</p>
            <p className="font-medium">{data.phone ?? ""}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-muted-foreground">Address</p>
            <p className="font-medium">{data.address ?? ""}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usage</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground">Users</p>
            <p className="font-medium">{Number(data.users_count ?? 0)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Sales</p>
            <p className="font-medium">{Number(data.sales_count ?? 0)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Active</p>
            <p className="font-medium">{data.is_active ? "Yes" : "No"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Created</p>
            <p className="font-medium">{data.created_at ?? ""}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

